<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LoadingIndicator from 'shared/components/LoadingIndicator.vue';
import { Item } from './Item.js';
import { Attribute } from './Attribute.js';
import { createItemSuggestion } from './itemSuggestion.js';
import ToolboxSidebar from './ToolboxSidebar.vue';
import StepDropZone from './StepDropZone.vue';
import ProcedureSkeletonLoader from './ProcedureSkeletonLoader.vue';
import ProcedureVersionModal from './ProcedureVersionModal.vue';
import StandardProcedureWizard from './StandardProcedureWizard.vue';
import StandardProcedureSteps from './StandardProcedureSteps.vue';
import htmlToMarkdownConverter from './HtmlToMarkdownConverter.js';
import markdownToHtmlConverter from './MarkdownToHtmlConverter.js';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AssistantsAPI from 'dashboard/api/assistants';
const props = defineProps({
  assistantId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

// Create item suggestion instance with store access
const itemSuggestion = createItemSuggestion(store);

// Character limits
const STEP_CHARACTER_LIMIT = 2000;

const procedures = ref([]);
const editingProcedureName = ref(-1);
const editingProcedureDescription = ref(-1);
const stepEditors = ref(new Map());
const isAutoSaving = ref(false);
const isLoading = ref(true);
const showDeleteProcedureConfirmation = ref(false);
const showDeleteStepConfirmation = ref(false);
const showDisconnectConfirmation = ref(false);
const selectedProcedureIndex = ref(null);
const selectedStepIndices = ref({ procedureIndex: null, stepIndex: null });
const expandedProcedureIndex = ref(null);
const showVersionHistory = ref(false);
const activeLabelDropdown = ref(null);
const activeProcedureMenu = ref(null);
const showStandardWizard = ref(false);
const showAddProcedureMenu = ref(false);
const standardWizardMode = ref('add');
const configuringProcedureIndex = ref(null);
const standardTemplates = ref([]);

function templateIdOf(procedure) {
  return procedure?.template_id || procedure?.templateId || '';
}

function inferredTemplate(procedure) {
  if (!procedure) return null;
  const templateId = templateIdOf(procedure);
  if (templateId) {
    return (
      standardTemplates.value.find(template => template.id === templateId) ||
      null
    );
  }
  const stepKeys = (procedure.steps || [])
    .map(step => step.key)
    .filter(Boolean);
  if (stepKeys.length) {
    return (
      standardTemplates.value.find(template => {
        const catalogKeys = (template.steps || []).map(step => step.key);
        return (
          catalogKeys.length === stepKeys.length &&
          catalogKeys.every(key => stepKeys.includes(key)) &&
          stepKeys.every(key => catalogKeys.includes(key))
        );
      }) || null
    );
  }
  return (
    standardTemplates.value.find(
      template =>
        template.name === procedure.name &&
        (template.steps || []).length === (procedure.steps || []).length
    ) || null
  );
}

function isStandard(procedure) {
  if (!procedure) return false;
  if (procedure.kind === 'custom') return false;
  if (procedure.kind === 'standard' || Boolean(templateIdOf(procedure))) {
    return true;
  }
  if ((procedure.steps || []).some(step => step.key)) return true;
  return Boolean(inferredTemplate(procedure));
}

function restoreStandardMetadata(procedure) {
  if (procedure?.kind === 'custom') return procedure;
  const template = inferredTemplate(procedure);
  const templateId = templateIdOf(procedure) || template?.id;
  if (procedure?.kind !== 'standard' && !templateId) return procedure;

  const restored = {
    ...procedure,
    kind: 'standard',
    template_id: templateId,
  };
  if (template) {
    restored.steps = restoreStepKeys(restored.steps, template.steps);
  }
  return restored;
}

function restoreStepKeys(steps, catalogSteps) {
  if (!steps?.length || !catalogSteps?.length) return steps;
  const unused = [...catalogSteps];
  return steps.map(step => {
    if (step.key) {
      const usedIndex = unused.findIndex(catalog => catalog.key === step.key);
      if (usedIndex >= 0) unused.splice(usedIndex, 1);
      return step;
    }
    const matchIndex = unused.findIndex(catalog =>
      instructionMatchesCatalog(step.instruction, catalog)
    );
    if (matchIndex < 0) return step;
    const [catalog] = unused.splice(matchIndex, 1);
    return { ...step, key: catalog.key };
  });
}

function instructionMatchesCatalog(instruction, catalog) {
  const text = instruction || '';
  const catalogText = catalog.instruction || '';
  const tokens = [
    ...catalogText.matchAll(
      /%\{(?:Tool|ContactAttribute|Attribute|EventAttribute):([^}]+)\}/g
    ),
  ].map(match => match[1]);
  if (tokens.some(token => text.includes(token))) return true;
  return Boolean(catalog.key) && text.includes(catalog.key);
}

function catalogFor(procedure) {
  return inferredTemplate(procedure);
}

function catalogStepFor(procedure, step) {
  return (catalogFor(procedure)?.steps || []).find(
    catalogStep => catalogStep.key === step.key
  );
}

function hasExplicitDisabled(step) {
  return (
    step &&
    Object.prototype.hasOwnProperty.call(step, 'disabled') &&
    step.disabled !== undefined
  );
}

function resolveStandardStepDisabled(procedure, step) {
  if (step.type !== 'optional') return false;
  if (hasExplicitDisabled(step)) return Boolean(step.disabled);
  return Boolean(catalogStepFor(procedure, step)?.default_disabled);
}

function normalizeStandardProcedure(procedure) {
  if (!isStandard(procedure)) return procedure;

  return {
    ...procedure,
    steps: (procedure.steps || []).map(step => ({
      ...step,
      disabled: resolveStandardStepDisabled(procedure, step),
    })),
  };
}

function addedTemplateIds() {
  return procedures.value
    .filter(procedure => isStandard(procedure))
    .map(procedure => templateIdOf(procedure));
}

function syncStandardProceduresFromStore() {
  const assistantData = store.getters['assistants/getAssistant'](
    props.assistantId
  );
  const serverProcedures = assistantData.procedures || [];
  serverProcedures.forEach((serverProc, index) => {
    const restored = restoreStandardMetadata(serverProc);
    const local = procedures.value[index];
    if (!isStandard(restored) || !local) return;

    const serverSteps = restored.steps || [];
    const steps = (local.steps || []).map(localStep => {
      const serverStep = serverSteps.find(
        step => step.key && localStep.key && step.key === localStep.key
      );
      const mergedStep = {
        ...localStep,
        instruction: serverStep?.instruction || localStep.instruction,
        attached_attributes:
          localStep.attached_attributes ||
          serverStep?.attached_attributes ||
          [],
      };
      if (serverStep && hasExplicitDisabled(serverStep)) {
        mergedStep.disabled = Boolean(serverStep.disabled);
      } else {
        mergedStep.disabled = resolveStandardStepDisabled(restored, {
          ...mergedStep,
          ...serverStep,
        });
      }
      return mergedStep;
    });

    procedures.value[index] = {
      ...restored,
      knowledgebases: restored.knowledgebases || restored.libraries || [],
      ui: restored.ui || [],
      steps,
    };
  });
}

function availableLabels(procedureIndex) {
  const procedure = procedures.value[procedureIndex];
  const currentLabels = procedure.conditions?.labels || [];
  const labels = store.getters['labels/getLabels'] || [];
  return labels.filter(l => !currentLabels.includes(l.title));
}

function toggleContactIdentified(procedureIndex) {
  const procedure = procedures.value[procedureIndex];
  if (!procedure.conditions) {
    procedure.conditions = {};
  }
  procedure.conditions.contact_identified =
    !procedure.conditions.contact_identified;
  debouncedAutoSave();
}

function toggleLabelDropdown(procedureIndex) {
  activeLabelDropdown.value =
    activeLabelDropdown.value === procedureIndex ? null : procedureIndex;
}

function addLabelCondition(procedureIndex, label) {
  const procedure = procedures.value[procedureIndex];
  if (!procedure.conditions) {
    procedure.conditions = {};
  }
  if (!procedure.conditions.labels) {
    procedure.conditions.labels = [];
  }
  if (procedure.conditions.labels.length >= 3) return;
  procedure.conditions.labels.push(label);
  activeLabelDropdown.value = null;
  debouncedAutoSave();
}

function removeLabelCondition(procedureIndex, label) {
  const procedure = procedures.value[procedureIndex];
  if (procedure.conditions?.labels) {
    procedure.conditions.labels = procedure.conditions.labels.filter(
      l => l !== label
    );
  }
  debouncedAutoSave();
}

// Helper functions
async function saveProcedure() {
  if (!props.assistantId) return;

  try {
    // Convert HTML content to markdown format before saving
    const proceduresForSaving = procedures.value.map(procedure => {
      const { visualizations, ...procedureWithoutVisualizations } = procedure;
      return {
        ...procedureWithoutVisualizations,
        // Ensure tools is always an array
        tools: Array.isArray(procedure.tools) ? procedure.tools : [],
        // Ensure attributes is always an array
        attributes: Array.isArray(procedure.attributes)
          ? procedure.attributes
          : [],
        knowledgebases: (Array.isArray(procedure.knowledgebases)
          ? procedure.knowledgebases
          : []
        ).map(kb =>
          kb && typeof kb === 'object' && kb.id != null ? { id: kb.id } : kb
        ),
        // Ensure ui is always an array
        ui: Array.isArray(procedure.ui) ? procedure.ui : [],
        steps: procedure.steps.map(step => {
          if (isStandard(procedure)) {
            return {
              key: step.key,
              type: step.type,
              instruction: step.instruction,
              attached_attributes: (step.attached_attributes || []).map(item =>
                typeof item === 'string' ? { key: item } : { key: item.key }
              ),
              disabled: Boolean(step.disabled),
            };
          }
          return {
            ...step,
            instruction: htmlToMarkdownConverter.convertToMarkdown(
              step.instruction
            ),
          };
        }),
      };
    });

    await store.dispatch('assistants/update', {
      id: props.assistantId,
      procedures: proceduresForSaving,
    });
    syncStandardProceduresFromStore();
  } catch (error) {
    useAlert(error.message);
  }
}

async function updateProcedureTools(procedureIndex) {
  const procedure = procedures.value[procedureIndex];
  if (!procedure || isStandard(procedure)) return;

  const toolNamesUsed = new Set();
  const toolConfigurations = [];
  const attributeKeysUsed = new Set();
  const attributeConfigurations = [];
  const contactAttributeKeysUsed = new Set();
  const contactAttributeConfigurations = [];
  const libraryNamespacesUsed = new Set();
  const libraryConfigurations = [];
  const uiIdsUsed = new Set();
  const uiConfigurations = [];

  procedure.steps.forEach(step => {
    // Extract tool references from step instruction HTML using the converter
    const toolReferences = htmlToMarkdownConverter.extractToolReferences(
      step.instruction
    );
    toolReferences.forEach(toolName => {
      toolNamesUsed.add(toolName);
    });

    // Extract attribute references from step instruction HTML using the converter
    const attributeReferences =
      htmlToMarkdownConverter.extractAttributeReferences(step.instruction);
    attributeReferences.forEach(attributeKey => {
      attributeKeysUsed.add(attributeKey);
    });

    // Extract contact attribute references from step instruction HTML using the converter
    const contactAttributeReferences =
      htmlToMarkdownConverter.extractContactAttributeReferences(
        step.instruction
      );

    contactAttributeReferences.forEach(contactAttributeKey => {
      contactAttributeKeysUsed.add(contactAttributeKey);
    });

    const eventAttributeReferences =
      htmlToMarkdownConverter.extractEventAttributeReferences(step.instruction);
    eventAttributeReferences.forEach(eventAttributeKey => {
      attributeKeysUsed.add(eventAttributeKey);
    });

    // Extract library references from step instruction HTML using the converter
    const libraryReferences = htmlToMarkdownConverter.extractLibraryReferences(
      step.instruction
    );
    libraryReferences.forEach(namespace => {
      libraryNamespacesUsed.add(namespace);
    });

    // Extract UI references from step instruction HTML using the converter
    const uiReferences = htmlToMarkdownConverter.extractVisualizationReferences(
      step.instruction
    );
    uiReferences.forEach(uiId => {
      uiIdsUsed.add(uiId);
    });

    // Also check for any existing markdown-format visualization references
    const visualizationMatches = step.instruction.match(
      /%\{Visualization:([a-zA-Z0-9_-]+)\}/g
    );
    if (visualizationMatches) {
      visualizationMatches.forEach(match => {
        const uiId = match.match(/%\{Visualization:([a-zA-Z0-9_-]+)\}/)[1];
        if (uiId) {
          uiIdsUsed.add(uiId);
        }
      });
    }

    // Also check for any existing markdown-format tool references
    const toolMatches = step.instruction.match(
      /%\{Tool:([a-zA-Z0-9_-]+)(?::([a-zA-Z0-9_-]+))?\}/g
    );
    if (toolMatches) {
      toolMatches.forEach(match => {
        const itemName = match.match(/%\{Tool:([a-zA-Z0-9_-]+)/)[1];
        if (itemName) {
          toolNamesUsed.add(itemName);
        }
      });
    }

    // Also check for any existing markdown-format attribute references
    const attributeMatches = step.instruction.match(
      /%\{Attribute:([a-zA-Z0-9_-]+)\}/g
    );
    if (attributeMatches) {
      attributeMatches.forEach(match => {
        const attributeName = match.match(/%\{Attribute:([a-zA-Z0-9_-]+)\}/)[1];
        if (attributeName) {
          attributeKeysUsed.add(attributeName);
        }
      });
    }

    // Also check for any existing markdown-format contact attribute references
    const contactAttributeMatches = step.instruction.match(
      /%\{ContactAttribute:([a-zA-Z0-9_-]+)\}/g
    );
    if (contactAttributeMatches) {
      contactAttributeMatches.forEach(match => {
        const contactAttributeName = match.match(
          /%\{ContactAttribute:([a-zA-Z0-9_-]+)\}/
        )[1];
        if (contactAttributeName) {
          contactAttributeKeysUsed.add(contactAttributeName);
        }
      });
    }

    const eventAttributeMatches = step.instruction.match(
      /%\{(?:EventAttribute|EventField):([a-zA-Z0-9_-]+)\}/g
    );
    if (eventAttributeMatches) {
      eventAttributeMatches.forEach(match => {
        const eventAttributeName = match.match(
          /%\{(?:EventAttribute|EventField):([a-zA-Z0-9_-]+)\}/
        )[1];
        if (eventAttributeName) {
          attributeKeysUsed.add(eventAttributeName);
        }
      });
    }

    // Also check for any existing markdown-format library references
    const libraryMatches = step.instruction.match(
      /%\{Library:([a-zA-Z0-9_-]+)\}/g
    );
    if (libraryMatches) {
      libraryMatches.forEach(match => {
        const namespace = match.match(/%\{Library:([a-zA-Z0-9_-]+)\}/)[1];
        if (namespace) {
          libraryNamespacesUsed.add(namespace);
        }
      });
    }
  });

  const { getAllUi, getRestaurantHooksByProvider } = await import('./ui.js');
  const getIntegration = store.getters['integrations/getIntegration'];
  const allUiElements = getAllUi(getRestaurantHooksByProvider(getIntegration));

  // UI elements backed by agent tools (e.g. ask_user inline form)
  uiIdsUsed.forEach(uiId => {
    const uiElement = allUiElements.find(ui => ui.id === uiId);
    if (uiElement?.toolName) {
      toolNamesUsed.add(uiElement.toolName);
    }
  });

  // Get full tool configurations for each referenced tool
  toolNamesUsed.forEach(toolName => {
    const toolConfig = store.getters['tools/getToolByName'](toolName);
    if (toolConfig) {
      // Store the complete tool configuration including MCP details
      const completeToolConfig = {
        name: toolConfig.name,
        description: toolConfig.description,
        arguments: toolConfig.arguments || [],
        type: toolConfig.type,
      };

      // Include MCP configuration if it exists
      if (toolConfig.mcp) {
        completeToolConfig.mcp = {
          uri: toolConfig.mcp.uri,
          credential_id: toolConfig.mcp.credential_id,
          server_id: toolConfig.mcp.server_id,
          server_label: toolConfig.mcp.server_label,
        };
      }

      toolConfigurations.push(completeToolConfig);
    } else {
      // Fallback for tools not found in store (legacy support)
      toolConfigurations.push(toolName);
    }
  });

  // Get attribute configurations for each referenced attribute
  // Convert attribute keys to JSON Schema format expected by the backend
  attributeKeysUsed.forEach(attributeKey => {
    // Create a basic JSON Schema attribute configuration
    // This follows the permitted_update_params structure for attributes
    const attributeConfig = {
      key: attributeKey,
      type: 'string', // Default type
      title: attributeKey
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()), // Convert snake_case to Title Case
      description: `Attribute: ${attributeKey}`,
      required: false, // Default to optional
    };

    // Try to get more detailed attribute information from store if available
    const attributeFromStore =
      store.getters['attributes/getAttributeByKey']?.(attributeKey);
    if (attributeFromStore) {
      // Map store attribute properties to JSON Schema format
      attributeConfig.type =
        attributeFromStore.attribute_display_type || 'string';
      attributeConfig.title =
        attributeFromStore.attribute_display_name || attributeConfig.title;
      attributeConfig.description =
        attributeFromStore.attribute_description || attributeConfig.description;

      // Add attribute values for list/checkbox types
      if (attributeFromStore.attribute_values?.length) {
        attributeConfig.enum = attributeFromStore.attribute_values;
      }

      // Map attribute display type to JSON Schema type
      const typeMapping = {
        text: 'string',
        number: 'number',
        personnel: 'number',
        date: 'string',
        time: 'string',
        list: 'string',
        checkbox: 'boolean',
        link: 'string',
      };

      if (typeMapping[attributeFromStore.attribute_display_type]) {
        attributeConfig.type =
          typeMapping[attributeFromStore.attribute_display_type];
      }

      // Add format for specific types
      if (attributeFromStore.attribute_display_type === 'date') {
        attributeConfig.format = 'date';
      } else if (attributeFromStore.attribute_display_type === 'time') {
        attributeConfig.format = 'time';
      } else if (attributeFromStore.attribute_display_type === 'link') {
        attributeConfig.format = 'uri';
      }
    }

    attributeConfigurations.push(attributeConfig);
  });

  // Fetch all contact attributes (both custom and standard)
  await store.dispatch('attributes/getAllContactAttributes');

  // Get contact attribute configurations for each referenced contact attribute
  contactAttributeKeysUsed.forEach(contactAttributeKey => {
    // Create a basic JSON Schema contact attribute configuration
    // This follows the permitted_update_params structure for contact attributes
    const contactAttributeConfig = {
      key: contactAttributeKey,
      type: 'string', // Default type
      title: contactAttributeKey
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase()), // Convert snake_case to Title Case
      description: `Contact Attribute: ${contactAttributeKey}`,
      required: false, // Default to optional
    };

    // Try to get more detailed contact attribute information from store if available
    const contactAttributes =
      store.getters['attributes/getAllContactAttributes'];

    const contactAttributeFromStore = contactAttributes.find(
      attr => attr.attribute_key === contactAttributeKey
    );

    if (contactAttributeFromStore) {
      // Map store contact attribute properties to JSON Schema format
      contactAttributeConfig.type =
        contactAttributeFromStore.attribute_display_type || 'string';
      contactAttributeConfig.title =
        contactAttributeFromStore.attribute_display_name ||
        contactAttributeConfig.title;
      contactAttributeConfig.description =
        contactAttributeFromStore.attribute_description ||
        contactAttributeConfig.description;

      // Add attribute values for list/checkbox types
      if (contactAttributeFromStore.attributeValues?.length) {
        contactAttributeConfig.enum = contactAttributeFromStore.attributeValues;
      }

      // Map attribute display type to JSON Schema type
      const typeMapping = {
        text: 'string',
        number: 'number',
        date: 'string',
        time: 'string',
        list: 'string',
        checkbox: 'boolean',
        link: 'string',
      };

      if (typeMapping[contactAttributeFromStore.attributeDisplayType]) {
        contactAttributeConfig.type =
          typeMapping[contactAttributeFromStore.attributeDisplayType];
      }

      // Add format for specific types
      if (contactAttributeFromStore.attributeDisplayType === 'date') {
        contactAttributeConfig.format = 'date';
      } else if (contactAttributeFromStore.attributeDisplayType === 'time') {
        contactAttributeConfig.format = 'time';
      } else if (contactAttributeFromStore.attributeDisplayType === 'link') {
        contactAttributeConfig.format = 'uri';
      }
    }

    contactAttributeConfigurations.push(contactAttributeConfig);
  });

  // Get library configurations for each referenced library
  libraryNamespacesUsed.forEach(namespace => {
    const allKnowledgebases = store.getters['knowledgebase/getLibraries'];

    const knowledgebase = allKnowledgebases.find(
      kb => kb.namespace === namespace
    );

    if (knowledgebase) {
      libraryConfigurations.push({
        namespace: namespace,
        name: knowledgebase.name,
        description: knowledgebase.description,
        id: knowledgebase.id,
        type: knowledgebase.type || 'documents',
      });
    } else {
      // Fallback for libraries not found in store
      libraryConfigurations.push({
        namespace: namespace,
        name: namespace,
        description: `Library: ${namespace}`,
        type: 'documents',
      });
    }
  });

  // Get UI configurations for each referenced UI element
  uiIdsUsed.forEach(uiId => {
    const uiElement = allUiElements.find(ui => ui.id === uiId);
    // Tool-backed UI (e.g. ask_user) is enabled via procedure.tools, not UI render nodes.
    if (uiElement && !uiElement.toolName) {
      uiConfigurations.push({
        id: uiElement.id,
        name: uiElement.name,
        label: uiElement.label,
        description: uiElement.description,
        schema: uiElement.schema,
      });
    }
  });

  procedure.tools = toolConfigurations; // Always an array since we push to toolConfigurations
  procedure.attributes = [
    ...attributeConfigurations,
    ...contactAttributeConfigurations,
  ];
  procedure.knowledgebases = libraryConfigurations;
  procedure.ui = uiConfigurations;
}

// Debounced autosave function
let saveTimeout = null;
const debouncedAutoSave = async procedureIndex => {
  if (saveTimeout) clearTimeout(saveTimeout);

  saveTimeout = setTimeout(async () => {
    isAutoSaving.value = true;
    const saveStartTime = Date.now();

    try {
      if (procedureIndex !== undefined) {
        await updateProcedureTools(procedureIndex);
      }
      await saveProcedure();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Auto-save failed:', error);
    } finally {
      // Ensure the saving indicator shows for at least 800ms
      const elapsed = Date.now() - saveStartTime;
      const minDisplayTime = 800;

      if (elapsed < minDisplayTime) {
        setTimeout(() => {
          isAutoSaving.value = false;
        }, minDisplayTime - elapsed);
      } else {
        isAutoSaving.value = false;
      }
    }
  }, 1000);
};

// Helper functions
function initializeStepEditor(procedureIndex, stepIndex) {
  const procedure = procedures.value[procedureIndex];
  if (!procedure || isStandard(procedure)) return;
  const stepKey = `${procedureIndex}-${stepIndex}`;
  const step = procedures.value[procedureIndex].steps[stepIndex];

  // Destroy existing editor if it exists
  const existingEditor = stepEditors.value.get(stepKey);
  if (existingEditor) {
    existingEditor.destroy();
  }

  const editor = new Editor({
    content: step.instruction,
    extensions: [
      StarterKit,
      Attribute,
      Item.configure({
        suggestion: {
          ...itemSuggestion,
          items: ({ query }) => {
            return itemSuggestion.items({ query });
          },
        },
      }),
    ],
    onUpdate: ({ editor: editorInstance }) => {
      step.instruction = editorInstance.getHTML();
      // Trigger autosave when content changes
      debouncedAutoSave(procedureIndex);
    },
  });

  stepEditors.value.set(stepKey, editor);
}

function initializeEditorsForProcedure(procedureIndex) {
  const procedure = procedures.value[procedureIndex];
  if (!procedure || isStandard(procedure)) return;
  procedure.steps.forEach((_, stepIndex) => {
    const stepKey = `${procedureIndex}-${stepIndex}`;
    if (!stepEditors.value.get(stepKey)) {
      initializeStepEditor(procedureIndex, stepIndex);
    }
  });
}

function expandProcedure(index) {
  expandedProcedureIndex.value = index;
  nextTick(() => {
    initializeEditorsForProcedure(index);
  });
}

function toggleExpand(index) {
  if (expandedProcedureIndex.value === index) {
    expandedProcedureIndex.value = null;
    return;
  }
  expandProcedure(index);
}

async function loadProcedures() {
  if (!props.assistantId) return;

  isLoading.value = true;

  try {
    // Load all necessary store data first to ensure icons and labels are available
    // when converting markdown to HTML
    await Promise.all([
      store.dispatch('assistants/show', props.assistantId),
      store.dispatch('tools/getAvailable'),
      store.dispatch('attributes/get'),
      store.dispatch('attributes/getAllContactAttributes'),
      store.dispatch('knowledgebase/get'),
      loadStandardTemplates(),
    ]);

    const assistantData = store.getters['assistants/getAssistant'](
      props.assistantId
    );

    // Convert markdown content to HTML for TipTap editors
    const proceduresData = assistantData.procedures || [];

    if (proceduresData.length === 0) {
      // Create an empty procedure with one empty step when there are no procedures
      procedures.value = [
        {
          name: 'General Questions',
          description:
            'How to answer general questions and provide helpful responses',
          steps: [
            {
              instruction: '<p></p>',
              type: 'required',
            },
          ],
          tools: [],
          attributes: [],
          contact_attributes: [],
          knowledgebases: [],
          ui: [],
        },
      ];

      // Set the procedure name to be in edit mode
      editingProcedureName.value = 0;
      expandProcedure(0);
    } else {
      procedures.value = proceduresData.map(procedure => {
        const restored = normalizeStandardProcedure(
          restoreStandardMetadata(procedure)
        );
        return {
          ...restored,
          // Handle backward compatibility: if procedure has 'libraries', map it to 'knowledgebases'
          knowledgebases: restored.knowledgebases || restored.libraries || [],
          ui: restored.ui || [],
          steps: restored.steps.map(step => ({
            ...step,
            instruction: isStandard(restored)
              ? step.instruction
              : markdownToHtmlConverter.convertToHtml(step.instruction, store),
          })),
        };
      });
    }

    // Initialize editors for all existing steps
    procedures.value.forEach((procedure, procedureIndex) => {
      procedure.steps.forEach((step, stepIndex) => {
        nextTick(() => {
          initializeStepEditor(procedureIndex, stepIndex);
        });
      });
    });

    // Update all procedures to ensure they have the latest tool/library configurations
    for (
      let procedureIndex = 0;
      procedureIndex < procedures.value.length;
      procedureIndex++
    ) {
      await updateProcedureTools(procedureIndex);
    }
  } catch (error) {
    useAlert(error.message);
  } finally {
    isLoading.value = false;
  }
}

// Main functions
function editProcedureName(index) {
  editingProcedureName.value = index;
  // Auto-focus the input field after it's rendered
  nextTick(() => {
    const input = document.querySelector(`input[data-edit-name="${index}"]`);
    if (input) {
      input.focus();
      input.select();
    }
  });
}

function addCustomProcedure() {
  showStandardWizard.value = false;
  const newProcedure = {
    name: 'New Procedure',
    description: '',
    steps: [
      {
        instruction: '<p>Describe the first required step.</p>',
        type: 'required',
      },
    ],
    tools: [],
    attributes: [],
    contact_attributes: [],
    knowledgebases: [],
    ui: [],
  };
  procedures.value.push(newProcedure);
  const procedureIndex = procedures.value.length - 1;
  expandProcedure(procedureIndex);

  // Initialize the editor for the new step
  nextTick(() => {
    initializeStepEditor(procedureIndex, 0);
  });

  editProcedureName(procedureIndex);
}

function openStandardWizard() {
  standardWizardMode.value = 'add';
  configuringProcedureIndex.value = null;
  showStandardWizard.value = true;
}

function selectAddProcedureType(type) {
  showAddProcedureMenu.value = false;
  if (type === 'custom') {
    addCustomProcedure();
  } else if (type === 'standard') {
    openStandardWizard();
  }
}

function openConfigureWizard(procedureIndex) {
  standardWizardMode.value = 'configure';
  configuringProcedureIndex.value = procedureIndex;
  showStandardWizard.value = true;
}

function closeStandardWizard() {
  showStandardWizard.value = false;
  configuringProcedureIndex.value = null;
  standardWizardMode.value = 'add';
}

function applyAttachedAttributes(steps, template, contactKeys, eventKeys) {
  return (steps || []).map(step => {
    const catalog = (template.steps || []).find(s => s.key === step.key);
    let attached = [];
    if (catalog?.configurable === 'contact') {
      attached = contactKeys.map(key => ({ key }));
    } else if (catalog?.configurable === 'event') {
      attached = eventKeys.map(key => ({ key }));
    }
    return {
      ...step,
      attached_attributes: attached,
    };
  });
}

function onStandardWizardComplete({
  template,
  contactAttributeKeys,
  eventAttributeKeys,
  knowledgebaseIds,
  procedureIndex,
  mode,
}) {
  closeStandardWizard();
  const knowledgebases = (knowledgebaseIds || []).map(id => ({ id }));

  if (mode === 'configure' && procedureIndex != null) {
    const procedure = procedures.value[procedureIndex];
    if (!procedure || !isStandard(procedure)) return;
    procedure.steps = applyAttachedAttributes(
      procedure.steps,
      template,
      contactAttributeKeys,
      eventAttributeKeys
    );
    procedure.knowledgebases = knowledgebases;
    expandProcedure(procedureIndex);
    saveProcedure();
    return;
  }

  if (addedTemplateIds().includes(template.id)) return;

  const steps = (template.steps || []).map(step => ({
    key: step.key,
    type: step.type,
    instruction: step.instruction || '',
    attached_attributes: [],
    ...(step.type === 'optional' && step.default_disabled
      ? { disabled: true }
      : {}),
  }));

  const newProcedure = {
    kind: 'standard',
    template_id: template.id,
    name: template.name,
    description: template.description,
    steps: applyAttachedAttributes(
      steps,
      template,
      contactAttributeKeys,
      eventAttributeKeys
    ),
    tools: [],
    attributes: [],
    knowledgebases,
    ui: [],
  };
  procedures.value.push(newProcedure);
  expandProcedure(procedures.value.length - 1);
  saveProcedure();
}

function moveStep(procedureIndex, from, to) {
  const procedure = procedures.value[procedureIndex];
  if (!procedure) return;
  if (to < 0 || to >= procedure.steps.length) return;

  if (!isStandard(procedure)) {
    procedure.steps.forEach((_, stepIndex) => {
      const editor = getStepEditor(procedureIndex, stepIndex);
      if (editor) {
        procedure.steps[stepIndex].instruction = editor.getHTML();
      }
    });
  }

  const [step] = procedure.steps.splice(from, 1);
  procedure.steps.splice(to, 0, step);

  if (!isStandard(procedure)) {
    nextTick(() => {
      procedure.steps.forEach((_, stepIndex) => {
        initializeStepEditor(procedureIndex, stepIndex);
      });
    });
  }

  debouncedAutoSave(procedureIndex);
}

function moveStandardStep({ procedureIndex, from, to }) {
  moveStep(procedureIndex, from, to);
}

function toggleStandardStepDisabled({ procedureIndex, stepIndex }) {
  const step = procedures.value[procedureIndex]?.steps[stepIndex];
  if (!step || step.type === 'required') return;
  step.disabled = !step.disabled;
  debouncedAutoSave(procedureIndex);
}

function saveProcedureName(index) {
  editingProcedureName.value = -1;
  // Trigger autosave when name is saved
  if (index !== undefined) {
    debouncedAutoSave(index);
  }
}

function editProcedureDescription(index) {
  editingProcedureDescription.value = index;
  // Auto-focus the input field after it's rendered
  nextTick(() => {
    const input = document.querySelector(
      `input[data-edit-description="${index}"]`
    );
    if (input) {
      input.focus();
      input.select();
    }
  });
}

function saveProcedureDescription(index) {
  editingProcedureDescription.value = -1;
  // Trigger autosave when description is saved
  if (index !== undefined) {
    debouncedAutoSave(index);
  }
}

function onProcedureNameClick(index, procedure) {
  if (expandedProcedureIndex.value === index && !isStandard(procedure)) {
    editProcedureName(index);
    return;
  }
  toggleExpand(index);
}

function onProcedureDescriptionClick(index, procedure) {
  if (expandedProcedureIndex.value === index && !isStandard(procedure)) {
    editProcedureDescription(index);
    return;
  }
  toggleExpand(index);
}

function deleteProcedure(index) {
  selectedProcedureIndex.value = index;
  showDeleteProcedureConfirmation.value = true;
}

function closeDeleteProcedurePopup() {
  showDeleteProcedureConfirmation.value = false;
  selectedProcedureIndex.value = null;
}

function disconnectStandardProcedure(index) {
  selectedProcedureIndex.value = index;
  showDisconnectConfirmation.value = true;
}

function closeDisconnectPopup() {
  showDisconnectConfirmation.value = false;
  selectedProcedureIndex.value = null;
}

async function confirmDisconnectProcedure() {
  const index = selectedProcedureIndex.value;
  if (index === null) {
    closeDisconnectPopup();
    return;
  }

  const procedure = procedures.value[index];
  if (!procedure || !isStandard(procedure)) {
    closeDisconnectPopup();
    return;
  }

  procedure.kind = 'custom';
  delete procedure.template_id;
  delete procedure.templateId;
  procedure.steps = (procedure.steps || []).map(step => ({
    instruction: markdownToHtmlConverter.convertToHtml(step.instruction, store),
    type: step.type || 'required',
  }));

  closeDisconnectPopup();
  expandProcedure(index);
  await nextTick();
  procedure.steps.forEach((_, stepIndex) => {
    initializeStepEditor(index, stepIndex);
  });
  await updateProcedureTools(index);
  await saveProcedure();
}

function confirmDeleteProcedure() {
  const index = selectedProcedureIndex.value;
  if (index !== null) {
    // Clean up editors for this procedure
    const procedure = procedures.value[index];
    procedure.steps.forEach((step, stepIndex) => {
      const stepKey = `${index}-${stepIndex}`;
      const editor = stepEditors.value.get(stepKey);
      if (editor) {
        editor.destroy();
        stepEditors.value.delete(stepKey);
      }
    });

    procedures.value.splice(index, 1);
    if (expandedProcedureIndex.value === index) {
      expandedProcedureIndex.value = null;
    } else if (expandedProcedureIndex.value > index) {
      expandedProcedureIndex.value -= 1;
    }
    // Trigger autosave to persist the deletion (no need to pass procedureIndex since it's deleted)
    debouncedAutoSave();
  }
  closeDeleteProcedurePopup();
}

function addStep(procedureIndex) {
  const newStep = {
    instruction: '<p></p>',
    type: 'required',
  };
  procedures.value[procedureIndex].steps.push(newStep);

  nextTick(() => {
    initializeStepEditor(
      procedureIndex,
      procedures.value[procedureIndex].steps.length - 1
    );
  });
}

function addStepAt(procedureIndex, insertIndex) {
  const newStep = {
    instruction: '<p></p>',
    type: 'required',
  };
  procedures.value[procedureIndex].steps.splice(insertIndex, 0, newStep);

  // Re-initialize all editors for this procedure since step indices have changed
  nextTick(() => {
    procedures.value[procedureIndex].steps.forEach((step, stepIndex) => {
      initializeStepEditor(procedureIndex, stepIndex);
    });
  });
}

function deleteStep(procedureIndex, stepIndex) {
  selectedStepIndices.value = { procedureIndex, stepIndex };
  showDeleteStepConfirmation.value = true;
}

function closeDeleteStepPopup() {
  showDeleteStepConfirmation.value = false;
  selectedStepIndices.value = { procedureIndex: null, stepIndex: null };
}

async function confirmDeleteStep() {
  const { procedureIndex, stepIndex } = selectedStepIndices.value;
  if (procedureIndex !== null && stepIndex !== null) {
    // Store content of editors that will need to be re-mapped
    const editorsToRemap = new Map();
    const procedure = procedures.value[procedureIndex];

    // Collect content from editors that come after the deleted step
    for (let i = stepIndex + 1; i < procedure.steps.length; i += 1) {
      const currentStepKey = `${procedureIndex}-${i}`;
      const editor = stepEditors.value.get(currentStepKey);
      if (editor) {
        editorsToRemap.set(i - 1, editor.getHTML()); // Store with new index
        editor.destroy();
        stepEditors.value.delete(currentStepKey);
      }
    }

    // Clean up the specific step editor that's being deleted
    const stepKey = `${procedureIndex}-${stepIndex}`;
    const editor = stepEditors.value.get(stepKey);
    if (editor) {
      editor.destroy();
      stepEditors.value.delete(stepKey);
    }

    // Remove the step from the procedure
    procedures.value[procedureIndex].steps.splice(stepIndex, 1);

    // Re-initialize editors for steps that came after the deleted one with their preserved content
    nextTick(() => {
      editorsToRemap.forEach((content, newStepIndex) => {
        // Update the step content first
        procedures.value[procedureIndex].steps[newStepIndex].instruction =
          content;
        // Then initialize the editor with the preserved content
        initializeStepEditor(procedureIndex, newStepIndex);
      });
    });

    await updateProcedureTools(procedureIndex);

    // Trigger autosave to persist the deletion
    debouncedAutoSave(procedureIndex);
  }
  closeDeleteStepPopup();
}

function toggleStepType(procedureIndex, stepIndex) {
  // Don't allow the first step to be changed to optional
  if (stepIndex === 0) return;

  const step = procedures.value[procedureIndex].steps[stepIndex];
  step.type = step.type === 'required' ? 'optional' : 'required';
  debouncedAutoSave(procedureIndex);
}

function getStepEditor(procedureIndex, stepIndex) {
  const stepKey = `${procedureIndex}-${stepIndex}`;
  return stepEditors.value.get(stepKey);
}

// Character count helpers
function getStepCharacterCount(procedureIndex, stepIndex) {
  const editor = getStepEditor(procedureIndex, stepIndex);
  if (!editor) return 0;
  return editor.state.doc.textContent.length;
}

function isStepOverLimit(procedureIndex, stepIndex) {
  return (
    getStepCharacterCount(procedureIndex, stepIndex) > STEP_CHARACTER_LIMIT
  );
}

function isStepNearLimit(procedureIndex, stepIndex) {
  return (
    getStepCharacterCount(procedureIndex, stepIndex) >
    STEP_CHARACTER_LIMIT * 0.8
  );
}

function getStepCharacterCountText(procedureIndex, stepIndex) {
  const count = getStepCharacterCount(procedureIndex, stepIndex);
  return `${count}`;
}

// Handle version history
function openVersionHistory() {
  showVersionHistory.value = true;
}

function closeVersionHistory() {
  showVersionHistory.value = false;
}

async function restoreAllProcedures({ procedures: restoredProcedures }) {
  if (!restoredProcedures || !Array.isArray(restoredProcedures)) {
    useAlert('Invalid procedure data');
    return;
  }

  try {
    // Clean up ALL existing editors
    stepEditors.value.forEach(editor => {
      editor.destroy();
    });
    stepEditors.value.clear();

    // Convert all restored procedures from markdown to HTML
    const restoredWithHtml = restoredProcedures.map(procedure => {
      const restored = normalizeStandardProcedure(
        restoreStandardMetadata(procedure)
      );
      return {
        ...restored,
        knowledgebases: restored.knowledgebases || restored.libraries || [],
        ui: restored.ui || [],
        steps: restored.steps.map(step => ({
          ...step,
          instruction: isStandard(restored)
            ? step.instruction
            : markdownToHtmlConverter.convertToHtml(step.instruction, store),
        })),
      };
    });

    // Replace ALL procedures
    procedures.value = restoredWithHtml;
    expandedProcedureIndex.value = null;

    // Re-initialize editors for all procedures and steps
    await nextTick();
    restoredWithHtml.forEach((procedure, procedureIndex) => {
      procedure.steps.forEach((step, stepIndex) => {
        initializeStepEditor(procedureIndex, stepIndex);
      });
    });

    // Update all procedure tools to ensure all references are current
    for (
      let procedureIndex = 0;
      procedureIndex < procedures.value.length;
      procedureIndex++
    ) {
      await updateProcedureTools(procedureIndex);
    }

    // Trigger autosave to persist the restored procedures
    debouncedAutoSave();

    useAlert('All procedures restored successfully');
  } catch (error) {
    useAlert('Failed to restore procedures: ' + error.message);
  }
}

// Handle tool drop from toolbox
function handleToolDrop({ tool, procedureIndex, stepIndex }) {
  const editor = getStepEditor(procedureIndex, stepIndex);
  if (!editor) return;

  // Focus the editor first to ensure it's active
  editor.commands.focus();

  // Use display name; prefix with integration when present (e.g. "Hubspot: Form: contact us")
  const baseLabel = tool.label || tool.title || tool.name || 'Unknown Tool';
  const integration =
    tool.meta?.integration ??
    tool.metadata?.Integration ??
    tool.meta?.Integration ??
    tool.metadata?.integration;
  const integrationLabel =
    integration &&
    String(integration).charAt(0).toUpperCase() +
      String(integration).slice(1).toLowerCase();
  const toolLabel = integrationLabel
    ? `${integrationLabel} » ${baseLabel}`
    : baseLabel;

  const itemAttrs = {
    id: tool.id,
    label: toolLabel,
    itemType: tool.itemType || 'tool',
    icon: tool.icon,
    action: tool.action,
  };

  // If it's an attribute, include attribute-specific properties
  if (
    tool.itemType === 'attribute' ||
    tool.itemType === 'contact_attribute' ||
    tool.itemType === 'event_attribute'
  ) {
    itemAttrs.attributeModel = tool.attributeModel;
    itemAttrs.attributeType = tool.attributeType;
  }

  // If it's a library, include namespace
  if (tool.itemType === 'library') {
    itemAttrs.namespace = tool.namespace;
  }

  // If it's a visualization, include visualization-specific properties
  if (tool.itemType === 'visualization') {
    itemAttrs.visualizationId = tool.id;
  }

  // Get current content to determine insertion position
  const currentContent = editor.getHTML();

  // If the step is empty or just has default content, replace it
  if (
    !currentContent ||
    currentContent === '<p></p>' ||
    currentContent.trim() === ''
  ) {
    // Insert item at the beginning of a new paragraph
    editor.commands.setContent([
      {
        type: 'paragraph',
        content: [
          {
            type: 'item',
            attrs: itemAttrs,
          },
          {
            type: 'text',
            text: ' ',
          },
        ],
      },
    ]);
  } else {
    // Get the current cursor position after focusing
    const { from, to, empty } = editor.state.selection;

    if (empty) {
      // Cursor is at a specific position - insert inline at that position
      editor.commands.insertContentAt(from, [
        {
          type: 'item',
          attrs: itemAttrs,
        },
        {
          type: 'text',
          text: ' ',
        },
      ]);

      // Position cursor after the inserted tool and space
      const newPosition = from + 2; // tool node + text node
      editor.commands.setTextSelection(newPosition);
    } else {
      // There's a selection - replace the selected text with the tool
      editor.commands.insertContentAt({ from, to }, [
        {
          type: 'item',
          attrs: itemAttrs,
        },
        {
          type: 'text',
          text: ' ',
        },
      ]);

      // Position cursor after the inserted tool and space
      const newPosition = from + 2; // tool node + text node
      editor.commands.setTextSelection(newPosition);
    }
  }

  // Ensure editor maintains focus after insertion
  setTimeout(() => {
    editor.commands.focus();
  }, 0);

  debouncedAutoSave(procedureIndex);
}

async function loadStandardTemplates() {
  try {
    const { data } = await AssistantsAPI.fetchStandardProcedures();
    standardTemplates.value = data.templates || [];
  } catch {
    standardTemplates.value = [];
  }
}

function toggleProcedureMenu(index) {
  activeProcedureMenu.value =
    activeProcedureMenu.value === index ? null : index;
}

function procedureActionItems(procedure) {
  const items = [];
  if (isStandard(procedure)) {
    items.push({
      action: 'configure',
      icon: 'i-lucide-settings-2',
      label: t('ASSISTANT_MGMT.PROCEDURES.STANDARD.CONFIGURE'),
      testId: 'configure-standard-procedure-button',
    });
    items.push({
      action: 'disconnect',
      icon: 'i-lucide-unlink',
      label: t('ASSISTANT_MGMT.PROCEDURES.STANDARD.DISCONNECT'),
      testId: 'disconnect-standard-procedure-button',
    });
  } else {
    items.push({
      action: 'edit',
      icon: 'i-lucide-pencil-line',
      label: t('ASSISTANT_MGMT.PROCEDURES.EDIT'),
      testId: 'edit-procedure-button',
    });
  }
  items.push({
    action: 'delete',
    icon: 'i-lucide-trash-2',
    label: t('ASSISTANT_MGMT.PROCEDURES.DELETE_PROCEDURE'),
    testId: 'procedure-delete-button',
  });
  return items;
}

function onProcedureMenuAction(index, action) {
  activeProcedureMenu.value = null;
  if (action === 'configure') {
    openConfigureWizard(index);
    return;
  }
  if (action === 'disconnect') {
    disconnectStandardProcedure(index);
    return;
  }
  if (action === 'edit') {
    expandProcedure(index);
    editProcedureName(index);
    return;
  }
  if (action === 'delete') {
    deleteProcedure(index);
  }
}

function closeOverlays() {
  activeLabelDropdown.value = null;
  activeProcedureMenu.value = null;
}

// Lifecycle
onMounted(() => {
  loadProcedures();
  document.addEventListener('click', closeOverlays);
});

onUnmounted(() => {
  // Clean up all editors
  stepEditors.value.forEach(editor => {
    editor.destroy();
  });
  stepEditors.value.clear();

  // Clear timeout
  if (saveTimeout) clearTimeout(saveTimeout);

  document.removeEventListener('click', closeOverlays);
});
</script>

<template>
  <div class="procedure-editor flex" data-testid="procedure-editor">
    <!-- Main Content -->
    <div class="flex-1 min-w-0">
      <SettingsSection
        :title="$t('ASSISTANT_MGMT.PROCEDURES.TITLE')"
        :sub-title="$t('ASSISTANT_MGMT.PROCEDURES.SUB_TEXT')"
        :show-border="true"
      >
        <template #headerActions>
          <div class="flex items-center gap-1">
            <div
              v-on-clickaway="() => (showAddProcedureMenu = false)"
              class="relative"
            >
              <NextButton
                icon="i-lucide-plus"
                sm
                data-testid="add-procedure-button"
                :label="$t('ASSISTANT_MGMT.PROCEDURES.ADD_NEW')"
                @click="showAddProcedureMenu = !showAddProcedureMenu"
              />
              <div
                v-if="showAddProcedureMenu"
                class="absolute top-full ltr:right-0 rtl:left-0 mt-1 z-50 w-72 p-1.5 bg-n-alpha-3 backdrop-blur-[100px] rounded-xl border border-slate-50 dark:border-slate-900 shadow-md"
                data-testid="add-procedure-type-menu"
              >
                <button
                  type="button"
                  class="flex items-start gap-3 w-full p-2.5 rounded-lg text-left cursor-pointer transition-colors hover:bg-n-alpha-2"
                  data-testid="add-procedure-type-standard"
                  @click="selectAddProcedureType('standard')"
                >
                  <Icon
                    icon="i-lucide-library"
                    class="size-5 mt-0.5 shrink-0 text-n-slate-11"
                  />
                  <span class="min-w-0">
                    <span class="block text-sm font-semibold text-n-slate-12">{{
                      $t('ASSISTANT_MGMT.PROCEDURES.ADD_TYPE.STANDARD')
                    }}</span>
                    <span class="block text-xs text-n-slate-11 mt-0.5">{{
                      $t(
                        'ASSISTANT_MGMT.PROCEDURES.ADD_TYPE.STANDARD_DESCRIPTION'
                      )
                    }}</span>
                  </span>
                </button>
                <button
                  type="button"
                  class="flex items-start gap-3 w-full p-2.5 rounded-lg text-left cursor-pointer transition-colors hover:bg-n-alpha-2"
                  data-testid="add-procedure-type-custom"
                  @click="selectAddProcedureType('custom')"
                >
                  <Icon
                    icon="i-lucide-pencil-line"
                    class="size-5 mt-0.5 shrink-0 text-n-slate-11"
                  />
                  <span class="min-w-0">
                    <span class="block text-sm font-semibold text-n-slate-12">{{
                      $t('ASSISTANT_MGMT.PROCEDURES.ADD_TYPE.CUSTOM')
                    }}</span>
                    <span class="block text-xs text-n-slate-11 mt-0.5">{{
                      $t(
                        'ASSISTANT_MGMT.PROCEDURES.ADD_TYPE.CUSTOM_DESCRIPTION'
                      )
                    }}</span>
                  </span>
                </button>
              </div>
            </div>
            <NextButton
              icon="i-lucide-clock"
              ghost
              data-testid="procedure-version-history-button"
              @click="openVersionHistory"
            />
          </div>
        </template>
        <!-- Autosave indicator -->
        <LoadingIndicator
          v-if="isAutoSaving"
          position="fixed"
          placement="bottom-right"
          variant="primary"
          size="md"
          :message="$t('ASSISTANT_MGMT.PROCEDURES.SAVING_CHANGES')"
        />

        <!-- Procedure List -->
        <div v-if="isLoading">
          <ProcedureSkeletonLoader />
        </div>
        <div v-else-if="procedures.length" class="mb-8 space-y-3">
          <div
            v-for="(procedure, index) in procedures"
            :key="procedure.id || index"
            class="procedure-card border border-n-slate-3 dark:border-n-slate-8 rounded-lg bg-white dark:bg-n-slate-2 shadow-sm"
            data-testid="procedure-card"
          >
            <div
              class="flex justify-between items-start gap-4 p-5 cursor-pointer"
              @click="toggleExpand(index)"
            >
              <div class="flex-1 min-w-0">
                <!-- Procedure Name -->
                <div class="relative group/name" @click.stop>
                  <input
                    v-if="
                      editingProcedureName === index && !isStandard(procedure)
                    "
                    v-model="procedure.name"
                    type="text"
                    :data-edit-name="index"
                    :placeholder="
                      $t('ASSISTANT_MGMT.PROCEDURES.NAME_PLACEHOLDER')
                    "
                    class="text-lg font-semibold bg-transparent border-b border-n-slate-6 focus:border-n-blue-9 outline-none w-full text-slate-900 dark:text-slate-100"
                    @blur="saveProcedureName(index)"
                    @keydown.enter="saveProcedureName(index)"
                    @keydown.escape="saveProcedureName(index)"
                  />
                  <div v-else class="flex items-center gap-2">
                    <h3
                      class="text-lg font-semibold text-slate-900 dark:text-slate-100"
                      :class="{
                        'hover:text-n-blue-11':
                          expandedProcedureIndex === index &&
                          !isStandard(procedure),
                      }"
                      @click="onProcedureNameClick(index, procedure)"
                    >
                      {{
                        procedure.name ||
                        $t('ASSISTANT_MGMT.PROCEDURES.UNTITLED')
                      }}
                    </h3>
                    <svg
                      v-if="
                        !isStandard(procedure) &&
                        expandedProcedureIndex === index
                      "
                      class="w-4 h-4 opacity-0 group-hover/name:opacity-100 transition-opacity text-n-slate-9 hover:text-n-blue-11 cursor-pointer"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      @click="editProcedureName(index)"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </div>
                </div>

                <!-- Procedure Description -->
                <div class="relative group/description" @click.stop>
                  <input
                    v-if="
                      editingProcedureDescription === index &&
                      !isStandard(procedure)
                    "
                    v-model="procedure.description"
                    type="text"
                    :data-edit-description="index"
                    :placeholder="
                      $t('ASSISTANT_MGMT.PROCEDURES.DESCRIPTION_PLACEHOLDER')
                    "
                    class="text-sm text-n-slate-10 mt-1 bg-transparent border-b border-n-slate-6 focus:border-n-blue-9 outline-none w-full"
                    @blur="saveProcedureDescription(index)"
                    @keydown.enter="saveProcedureDescription(index)"
                    @keydown.escape="saveProcedureDescription(index)"
                  />
                  <div v-else class="flex items-center gap-2 mt-1">
                    <p
                      class="text-sm text-n-slate-10 truncate"
                      :class="{
                        'hover:text-n-blue-10':
                          expandedProcedureIndex === index &&
                          !isStandard(procedure),
                      }"
                      @click="onProcedureDescriptionClick(index, procedure)"
                    >
                      {{
                        procedure.description ||
                        $t('ASSISTANT_MGMT.PROCEDURES.NO_DESCRIPTION')
                      }}
                    </p>
                    <svg
                      v-if="
                        !isStandard(procedure) &&
                        expandedProcedureIndex === index
                      "
                      class="w-3 h-3 opacity-0 group-hover/description:opacity-100 transition-opacity text-n-slate-8 hover:text-n-blue-10 flex-shrink-0 cursor-pointer"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      @click="editProcedureDescription(index)"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div class="flex items-start gap-2 shrink-0">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium mt-1.5"
                  :class="
                    isStandard(procedure)
                      ? 'bg-n-blue-2 text-n-blue-11 dark:bg-n-blue-3 dark:text-n-blue-10'
                      : 'bg-n-slate-2 text-n-slate-11 dark:bg-n-slate-7 dark:text-n-slate-11'
                  "
                >
                  {{
                    isStandard(procedure)
                      ? $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.BADGE')
                      : $t('ASSISTANT_MGMT.PROCEDURES.CUSTOM_BADGE')
                  }}
                </span>
                <NextButton
                  :icon="
                    expandedProcedureIndex === index
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  ghost
                  sm
                  data-testid="procedure-expand-button"
                  @click.stop="toggleExpand(index)"
                />
                <div
                  v-on-clickaway="() => (activeProcedureMenu = null)"
                  class="relative"
                  @click.stop
                >
                  <NextButton
                    icon="i-lucide-ellipsis"
                    ghost
                    sm
                    data-testid="procedure-actions-button"
                    @click="toggleProcedureMenu(index)"
                  />
                  <div
                    v-if="activeProcedureMenu === index"
                    class="absolute top-full ltr:right-0 rtl:left-0 mt-1 z-50 min-w-[12rem] p-1.5 bg-white dark:bg-n-solid-2 rounded-xl border border-n-slate-3 dark:border-n-slate-8 shadow-md"
                  >
                    <button
                      v-for="item in procedureActionItems(procedure)"
                      :key="item.action"
                      type="button"
                      class="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-lg text-left text-sm transition-colors hover:bg-n-alpha-2"
                      :class="
                        item.action === 'delete'
                          ? 'text-n-ruby-11'
                          : 'text-n-slate-12'
                      "
                      :data-testid="item.testId"
                      @click="onProcedureMenuAction(index, item.action)"
                    >
                      <Icon :icon="item.icon" class="size-4 shrink-0" />
                      {{ item.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="expandedProcedureIndex === index"
              class="px-5 pb-5"
              @click.stop
            >
              <div class="border-t border-n-slate-3 dark:border-n-slate-8 pt-4">
                <div class="flex flex-wrap items-center gap-2 mb-4">
                  <span
                    v-for="label in procedure.conditions?.labels || []"
                    :key="label"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-n-teal-2 text-n-teal-11 dark:bg-n-teal-3 dark:text-n-teal-10"
                  >
                    {{ label }}
                    <button
                      class="i-lucide-x w-3 h-3 hover:text-n-red-10 transition-colors"
                      @click="removeLabelCondition(index, label)"
                    />
                  </span>

                  <button
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
                    :class="
                      procedure.conditions?.contact_identified
                        ? 'bg-n-blue-2 text-n-blue-11 dark:bg-n-blue-3 dark:text-n-blue-10'
                        : 'bg-n-slate-2 text-n-slate-10 dark:bg-n-slate-7 dark:text-n-slate-11 hover:bg-n-slate-3'
                    "
                    :title="
                      $t(
                        'ASSISTANT_MGMT.PROCEDURES.CONDITIONS.IDENTIFIED_TOOLTIP'
                      )
                    "
                    @click="toggleContactIdentified(index)"
                  >
                    <span class="i-lucide-user-check w-3.5 h-3.5" />
                    {{
                      $t(
                        'ASSISTANT_MGMT.PROCEDURES.CONDITIONS.IDENTIFIED_LABEL'
                      )
                    }}
                  </button>

                  <div
                    v-if="(procedure.conditions?.labels || []).length < 3"
                    class="relative"
                    @click.stop
                  >
                    <button
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-n-slate-2 text-n-slate-10 dark:bg-n-slate-7 dark:text-n-slate-11 hover:bg-n-slate-3 transition-colors"
                      @click="toggleLabelDropdown(index)"
                    >
                      <span class="i-lucide-tag w-3.5 h-3.5" />
                      {{ $t('ASSISTANT_MGMT.PROCEDURES.CONDITIONS.ADD_LABEL') }}
                    </button>
                    <div
                      v-if="activeLabelDropdown === index"
                      class="absolute top-full left-0 mt-1 z-10 bg-white dark:bg-n-slate-2 border border-n-slate-3 dark:border-n-slate-7 rounded-lg shadow-lg py-1 min-w-[180px] max-h-48 overflow-y-auto"
                    >
                      <button
                        v-for="label in availableLabels(index)"
                        :key="label.id"
                        class="w-full text-left px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-slate-3 transition-colors"
                        @click="addLabelCondition(index, label.title)"
                      >
                        {{ label.title }}
                      </button>
                      <p
                        v-if="availableLabels(index).length === 0"
                        class="px-3 py-1.5 text-xs text-n-slate-9"
                      >
                        {{
                          $t(
                            'ASSISTANT_MGMT.PROCEDURES.CONDITIONS.NO_LABELS_AVAILABLE'
                          )
                        }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Steps -->
                <StandardProcedureSteps
                  v-if="isStandard(procedure)"
                  :procedure="procedure"
                  :procedure-index="index"
                  :catalog-template="catalogFor(procedure)"
                  @move-step="moveStandardStep"
                  @toggle-disabled="toggleStandardStepDisabled"
                />
                <div v-else class="steps-container">
                  <div
                    v-for="(step, stepIndex) in procedure.steps"
                    :key="stepIndex"
                  >
                    <!-- Add Step Between Button (appears between steps on hover) -->
                    <div
                      v-if="stepIndex > 0"
                      class="add-step-between relative group/between h-8 flex items-center justify-center my-4 cursor-pointer"
                      @click="addStepAt(index, stepIndex)"
                    >
                      <!-- Background line -->
                      <div
                        class="absolute inset-0 flex items-center pointer-events-none"
                      >
                        <div
                          class="w-full h-px bg-n-slate-4 dark:bg-n-slate-6"
                        />
                      </div>

                      <!-- Button and label container -->
                      <div
                        class="relative flex items-center gap-2 bg-white dark:bg-n-slate-1 px-4 py-2 rounded-full opacity-0 group-hover/between:opacity-100 transition-all duration-200 border border-n-slate-3 dark:border-n-slate-7"
                      >
                        <NextButton
                          icon="i-lucide-plus"
                          md
                          ghost
                          class="!w-5 !h-5 !p-0"
                        />
                        <span
                          class="text-n-slate-10 font-medium whitespace-nowrap"
                        >
                          {{ $t('ASSISTANT_MGMT.PROCEDURES.ADD_STEP_HERE') }}
                        </span>
                      </div>
                    </div>

                    <div
                      class="step-container group mb-3 mt-4 bg-white dark:bg-n-slate-2 rounded-lg transition-colors relative"
                    >
                      <div class="flex justify-between items-center mb-3">
                        <div class="flex items-center gap-3">
                          <h5
                            class="text-sm font-bold text-n-slate-11 dark:text-n-slate-10"
                          >
                            {{
                              $t('ASSISTANT_MGMT.PROCEDURES.STEP_NUMBER', {
                                number: stepIndex + 1,
                              })
                            }}
                          </h5>
                          <span
                            class="inline-block px-2 py-1 text-xs rounded transition-colors"
                            :class="[
                              step.type === 'required'
                                ? 'bg-slate-500 dark:bg-slate-800 text-slate-50 dark:text-slate-100 font-bold'
                                : 'bg-slate-50 dark:bg-slate-950 text-n-slate-12 font-bold',
                              stepIndex === 0
                                ? 'cursor-not-allowed opacity-75'
                                : 'cursor-pointer',
                            ]"
                            @click="
                              stepIndex !== 0 &&
                                toggleStepType(index, stepIndex)
                            "
                          >
                            {{
                              step.type === 'required'
                                ? $t(
                                    'ASSISTANT_MGMT.PROCEDURES.STEP_TYPES.REQUIRED'
                                  )
                                : $t(
                                    'ASSISTANT_MGMT.PROCEDURES.STEP_TYPES.OPTIONAL'
                                  )
                            }}
                          </span>
                        </div>
                        <div class="flex items-center gap-1">
                          <template v-if="procedure.steps.length > 1">
                            <NextButton
                              icon="i-lucide-arrow-up-from-line"
                              ghost
                              sm
                              :disabled="stepIndex === 0"
                              :title="$t('ASSISTANT_MGMT.PROCEDURES.MOVE_UP')"
                              @click="moveStep(index, stepIndex, stepIndex - 1)"
                            />
                            <NextButton
                              icon="i-lucide-arrow-down-from-line"
                              ghost
                              sm
                              :disabled="
                                stepIndex === procedure.steps.length - 1
                              "
                              :title="$t('ASSISTANT_MGMT.PROCEDURES.MOVE_DOWN')"
                              @click="moveStep(index, stepIndex, stepIndex + 1)"
                            />
                          </template>
                          <div
                            class="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <NextButton
                              icon="i-lucide-x"
                              ghost
                              sm
                              @click="deleteStep(index, stepIndex)"
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Step Editor with Drop Zone -->
                      <StepDropZone
                        :procedure-index="index"
                        :step-index="stepIndex"
                        @tool-dropped="handleToolDrop"
                      >
                        <div class="step-editor">
                          <div
                            class="editor-container px-3 border border-n-slate-6 dark:border-n-slate-7 rounded-lg min-h-[120px] focus-within:border-n-blue-9 transition-colors bg-white dark:bg-n-slate-2 relative"
                          >
                            <EditorContent
                              :editor="getStepEditor(index, stepIndex)"
                            />
                            <div
                              class="absolute bottom-2 right-3 bg-white/90 dark:bg-n-slate-11/90 backdrop-blur-sm rounded-md px-2 py-1 text-xs font-medium pointer-events-none z-10 shadow-sm border border-n-slate-3 dark:border-n-slate-8"
                            >
                              <span
                                :class="{
                                  'text-n-slate-10 dark:text-n-slate-10':
                                    !isStepNearLimit(index, stepIndex) &&
                                    !isStepOverLimit(index, stepIndex),
                                  'text-amber-600 dark:text-amber-400':
                                    isStepNearLimit(index, stepIndex) &&
                                    !isStepOverLimit(index, stepIndex),
                                  'text-red-500 dark:text-red-400':
                                    isStepOverLimit(index, stepIndex),
                                }"
                              >
                                {{
                                  getStepCharacterCountText(index, stepIndex)
                                }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </StepDropZone>
                    </div>
                  </div>

                  <!-- Add Step Button -->
                  <NextButton
                    icon="i-lucide-plus"
                    :label="$t('ASSISTANT_MGMT.PROCEDURES.ADD_STEP')"
                    md
                    @click="addStep(index)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </SettingsSection>
    </div>

    <!-- Toolbox Sidebar -->
    <ToolboxSidebar />

    <!-- Delete Procedure Confirmation Modal -->
    <woot-delete-modal
      v-model:show="showDeleteProcedureConfirmation"
      :on-close="closeDeleteProcedurePopup"
      :on-confirm="confirmDeleteProcedure"
      :title="$t('ASSISTANT_MGMT.DELETE.PROCEDURES.TITLE')"
      :message="$t('ASSISTANT_MGMT.DELETE.PROCEDURES.MESSAGE')"
      :confirm-text="$t('ASSISTANT_MGMT.DELETE.PROCEDURES.YES')"
      :reject-text="$t('ASSISTANT_MGMT.DELETE.PROCEDURES.NO')"
    />

    <woot-delete-modal
      v-model:show="showDisconnectConfirmation"
      :on-close="closeDisconnectPopup"
      :on-confirm="confirmDisconnectProcedure"
      :title="$t('ASSISTANT_MGMT.PROCEDURES.STANDARD.DISCONNECT_CONFIRM.TITLE')"
      :message="
        $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.DISCONNECT_CONFIRM.MESSAGE')
      "
      :confirm-text="
        $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.DISCONNECT_CONFIRM.YES')
      "
      :reject-text="
        $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.DISCONNECT_CONFIRM.NO')
      "
    />

    <!-- Delete Step Confirmation Modal -->
    <woot-delete-modal
      v-model:show="showDeleteStepConfirmation"
      :on-close="closeDeleteStepPopup"
      :on-confirm="confirmDeleteStep"
      :title="$t('ASSISTANT_MGMT.DELETE.STEPS.TITLE')"
      :message="$t('ASSISTANT_MGMT.DELETE.STEPS.MESSAGE')"
      :confirm-text="$t('ASSISTANT_MGMT.DELETE.STEPS.YES')"
      :reject-text="$t('ASSISTANT_MGMT.DELETE.STEPS.NO')"
    />

    <!-- Procedure Version History Modal -->
    <ProcedureVersionModal
      :show="showVersionHistory"
      :assistant-id="assistantId"
      :current-procedures="procedures"
      @close="closeVersionHistory"
      @restore="restoreAllProcedures"
    />

    <StandardProcedureWizard
      :show="showStandardWizard"
      :templates="standardTemplates"
      :added-template-ids="addedTemplateIds()"
      :mode="standardWizardMode"
      :procedure="
        configuringProcedureIndex != null
          ? procedures[configuringProcedureIndex]
          : null
      "
      :procedure-index="configuringProcedureIndex"
      @close="closeStandardWizard"
      @complete="onStandardWizardComplete"
    />
  </div>
</template>

<style scoped>
.procedure-editor {
  max-width: 100%;
  margin: 0 auto;
  height: calc(100vh - 178px);
  overflow-y: auto;
}

.prose {
  max-width: none;
}

.prose h1,
.prose h2,
.prose h3,
.prose h4,
.prose h5,
.prose h6 {
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.prose p {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}

.editor-container {
  position: relative;
}
</style>

<style>
.tiptap {
  outline: none;
  padding: 12px;
  min-height: 120px;
  overflow: hidden;
}

.tiptap:first-child {
  margin-top: 0;
}

.tiptap ul,
.tiptap ol {
  padding: 0 1rem;
  margin: 1.25rem 1rem 1.25rem 0.4rem;
}

.tiptap ul li p,
.tiptap ol li p {
  margin-top: 0.25em;
  margin-bottom: 0.25em;
}

.tiptap h1,
.tiptap h2,
.tiptap h3,
.tiptap h4,
.tiptap h5,
.tiptap h6 {
  line-height: 1.1;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.tiptap h1 {
  font-size: 1.4rem;
}

.tiptap h2 {
  font-size: 1.2rem;
}

.tiptap h3 {
  font-size: 1.1rem;
}

.tiptap code {
  background-color: rgba(97, 97, 97, 0.1);
  border-radius: 0.4rem;
  color: #616161;
  font-size: 0.85rem;
  padding: 0.25em 0.3em;
}

.tiptap pre {
  background: #0d0d0d;
  border-radius: 0.5rem;
  color: #fff;
  font-family: 'JetBrainsMono', monospace;
  margin: 1.5rem 0;
  padding: 0.75rem 1rem;
}

.tiptap pre code {
  background: none;
  color: inherit;
  font-size: 0.8rem;
  padding: 0;
}

.tiptap blockquote {
  border-left: 3px solid rgba(13, 13, 13, 0.1);
  margin: 1.5rem 0;
  padding-left: 1rem;
}

.tiptap hr {
  border: none;
  border-top: 1px solid rgba(13, 13, 13, 0.1);
  margin: 2rem 0;
}

.tiptap p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

/* Tool reference styles */
.tiptap .tool-reference,
.tiptap .item-reference {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background-color: #ebe4ff;
  color: #6550b9;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  display: inline-flex;
  gap: 8px;
}

.tiptap .tool-reference:hover,
.tiptap .item-reference:hover {
  transform: translateY(-1px);
}

.dark .tiptap .tool-reference,
.dark .tiptap .item-reference {
  background-color: #33255b;
}

/* Tool type and action styles */
.tiptap .tool-reference .tool-type,
.tiptap .tool-reference .item-type,
.tiptap .item-reference .tool-type,
.tiptap .item-reference .item-type {
  background: white;
  border-radius: 0.75rem;
  padding: 0.125rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  border: 1px solid #ebe4ff;
  color: #6550b9;
}

.dark .tiptap .tool-reference .tool-type,
.dark .tiptap .tool-reference .item-type,
.dark .tiptap .item-reference .tool-type,
.dark .tiptap .item-reference .item-type {
  border: 1px solid #33255b;
  color: #33255b;
}

.tiptap .tool-reference .tool-action,
.tiptap .tool-reference .item-action,
.tiptap .item-reference .tool-action,
.tiptap .item-reference .item-action {
  font-weight: 500;
  border-radius: 0.25rem;
  padding-right: 0.5rem;
  color: #6550b9;
}

.dark .tiptap .tool-reference .tool-action,
.dark .tiptap .tool-reference .item-action,
.dark .tiptap .item-reference .tool-action,
.dark .tiptap .item-reference .item-action {
  color: #baa7ff;
}

/* Attribute-specific styling */
.tiptap .item-reference[data-item-type='attribute'],
.tiptap .item-reference[data-item-type='contact_attribute'],
.tiptap .item-reference[data-item-type='event_attribute'],
.tiptap .item-reference[data-item-type='event_field'] {
  background-color: #ffecb7;
  color: #915930;
}

.dark .tiptap .item-reference[data-item-type='attribute'],
.dark .tiptap .item-reference[data-item-type='contact_attribute'],
.dark .tiptap .item-reference[data-item-type='event_attribute'],
.dark .tiptap .item-reference[data-item-type='event_field'] {
  background-color: #39270f;
  color: #ffcc4d;
}

.tiptap .item-reference[data-item-type='attribute'] .item-type,
.tiptap .item-reference[data-item-type='contact_attribute'] .item-type,
.tiptap .item-reference[data-item-type='event_attribute'] .item-type,
.tiptap .item-reference[data-item-type='event_field'] .item-type {
  background: white;
  border: 1px solid #ffecb7;
  color: #915930;
}

.dark .tiptap .item-reference[data-item-type='attribute'] .item-type,
.dark .tiptap .item-reference[data-item-type='contact_attribute'] .item-type,
.dark .tiptap .item-reference[data-item-type='event_attribute'] .item-type,
.dark .tiptap .item-reference[data-item-type='event_field'] .item-type {
  background: white;
  border: 1px solid #39270f;
  color: #39270f;
}

.tiptap .item-reference[data-item-type='attribute'] .item-action,
.tiptap .item-reference[data-item-type='contact_attribute'] .item-action,
.tiptap .item-reference[data-item-type='event_attribute'] .item-action,
.tiptap .item-reference[data-item-type='event_field'] .item-action {
  color: #915930;
}

.dark .tiptap .item-reference[data-item-type='attribute'] .item-action,
.dark .tiptap .item-reference[data-item-type='contact_attribute'] .item-action,
.dark .tiptap .item-reference[data-item-type='event_attribute'] .item-action,
.dark .tiptap .item-reference[data-item-type='event_field'] .item-action {
  color: #ffcc4d;
}

/* Attribute pill styles */
.tiptap .attribute-pill {
  display: inline-flex;
  align-items: center;
  background-color: #e0f2fe;
  color: #0284c7;
  border-radius: 1rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  transition: all 0.2s ease;
  cursor: help;
  position: relative;
}

.tiptap .attribute-pill:hover {
  transform: translateY(-1px);
  background-color: #bae6fd;
}

/* Custom tooltip styles */
.tiptap .attribute-pill[data-tooltip]:not([data-tooltip='']):hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 8px 12px;
  background-color: #1f2937;
  color: white;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.2;
  white-space: nowrap;
  max-width: 200px;
  white-space: normal;
  word-wrap: break-word;
  z-index: 1000;
  pointer-events: none;
}

/* Tooltip arrow */
.tiptap .attribute-pill[data-tooltip]:not([data-tooltip='']):hover::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 2px;
  border: 4px solid transparent;
  border-top-color: #1f2937;
  z-index: 1001;
  pointer-events: none;
}

.dark .tiptap .attribute-pill {
  background-color: #1e3a8a;
  color: #dbeafe;
}

.dark .tiptap .attribute-pill:hover {
  background-color: #1d4ed8;
}

/* Library-specific styling */
.tiptap .item-reference[data-item-type='library'] {
  background-color: #eef4ff;
  color: #1e40af;
}

.dark .tiptap .item-reference[data-item-type='library'] {
  background-color: #1e3a8a;
  color: #93c5fd;
}

.tiptap .item-reference[data-item-type='library'] .item-type {
  background: white;
  border: 1px solid #c7d2fe;
  color: #1e40af;
}

.dark .tiptap .item-reference[data-item-type='library'] .item-type {
  background: white;
  border: 1px solid #1e3a8a;
  color: #1e3a8a;
}

.tiptap .item-reference[data-item-type='library'] .item-action {
  color: #1e40af;
}

.dark .tiptap .item-reference[data-item-type='library'] .item-action {
  color: #93c5fd;
}

/* Visualization-specific styling */
.tiptap .item-reference[data-item-type='visualization'] {
  background-color: #ccfbf1;
  color: #115e59;
}

.dark .tiptap .item-reference[data-item-type='visualization'] {
  background-color: #134e4a;
  color: #5eead4;
}

.tiptap .item-reference[data-item-type='visualization'] .item-type {
  background: white;
  border: 1px solid #99f6e4;
  color: #115e59;
}

.dark .tiptap .item-reference[data-item-type='visualization'] .item-type {
  background: white;
  border: 1px solid #134e4a;
  color: #134e4a;
}

.tiptap .item-reference[data-item-type='visualization'] .item-action {
  color: #115e59;
}

.dark .tiptap .item-reference[data-item-type='visualization'] .item-action {
  color: #5eead4;
}
</style>
